import { z } from "zod";
import {
  belongsToEnum,
  genderSchema,
  incomeSlabSchema,
  occupationSchema,
  pepDetailsSchema,
  sourceOfWealthSchema,
} from "./onboarding";
import { ClientHoldingPatternSchema } from "../client";

export type PepDetails = z.infer<typeof pepDetailsSchema>;

export const userTypeSchema = z.enum(["investor", "partner"]);
export type UserType = z.infer<typeof userTypeSchema>;

export const autofillSourceSchema = z
  .enum(["HYPERVERGE", "MF_CENTRAL"])
  .nullable();
export type AutofillSource = z.infer<typeof autofillSourceSchema>;

export const investorProfileDetailsSchema = z.object({
  name: z.string(),
  date_of_birth: z.string(),
  gender: genderSchema,
  occupation: occupationSchema,
  pan: z.string(),
  source_of_wealth: sourceOfWealthSchema,
  income_slab: incomeSlabSchema,
  pep_details: pepDetailsSchema,
  investment_account: z.string(),
  country_of_birth: z.string(),
  autofilled: z.boolean(),
  autofill_source: autofillSourceSchema,
});

export const investorAddressDetailsSchema = z.object({
  line1: z.string(),
  line2: z.string(),
  line3: z.string(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country: z.string(),
  investment_account: z.string(),
  autofilled: z.boolean(),
  autofill_source: autofillSourceSchema,
});

export const investorBankDetailsSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  modified_at: z.string(),
  bank_account_guid: z.string(),
  primary_account_holder_name: z.string(),
  account_number: z.string(),
  ifsc_code: z.string(),
  type: z.string(),
  is_default: z.boolean(),
  branch_name: z.string(),
  bank_name: z.string(),
  branch_address: z.string(),
  branch_contact_number: z.string(),
  branch_city: z.string(),
  branch_district: z.string(),
  branch_state: z.string(),
  investment_account: z.string(),
  verification_status: z.enum(["unverified", "verified", "failed"]),
  verification_remark: z.string().nullable(),
  verified_at: z.string().nullable(),
  autofilled: z.boolean(),
  autofill_source: autofillSourceSchema,
});

export const InvestmentAccountStatusSchema = z.enum(["PENDING", "CREATED"]);

export type InvestmentAccountStatus = z.infer<
  typeof InvestmentAccountStatusSchema
>;

export const investorInvestmentAccountSchema = z.object({
  id: z.string(),
  holding_pattern: ClientHoldingPatternSchema,
  status: InvestmentAccountStatusSchema,
  client_id: z.number(),
  nomination_opt: z.literal("Y").or(z.literal("N")).or(z.literal("NA")),
  nominee_soa: z.literal("Y").or(z.literal("N")),
  primary_investor_guid: z.string(),
  investor_user_guid: z.string(),
  client_code: z.string(),
});

export const investorContactInfoSchema = z.object({
  contact_guid: z.string(),
  investment_account: z.string(),
  isd: z.string(),
  number: z.string(),
  email: z.string(),
  phone_belongs_to: belongsToEnum,
  email_belongs_to: belongsToEnum,
  created_at: z.string(),
  autofilled: z.boolean(),
  autofill_source: autofillSourceSchema,
});

export const investorNomineeDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  relationship: z.string(),
  date_of_birth: z.string().optional().nullable(),
  applicable_percentage: z.number(),
  identity_type: z.string(),
  identity_number: z.string(),
  email: z.string(),
  mobile: z.string(),
  address_line_1: z.string(),
  address_line_2: z.string().optional().nullable(),
  address_line_3: z.string().optional().nullable(),
  city: z.string(),
  pin_code: z.string(),
  country: z.string(),
  state: z.string(),
  guardian_name: z.string().optional().nullable(),
  guardian_pan: z.string().optional().nullable(),
  autofilled: z.boolean(),
  autofill_source: autofillSourceSchema,
});
